<template>
    <v-card :loading="loading">
        <v-card-title>
            <v-btn small icon @click="close" color="primary" class="mr-2">
                <v-icon>
                    mdi-close
                </v-icon>
            </v-btn>
            <h3>Milestone</h3>
        </v-card-title>
        <v-divider class="ml-4 mr-5" />
        <v-card-text>
            <v-form v-model="valid" class="mt-2 mb-0">
                <v-row cols="12" no-gutters>
                    <v-col cols="8" class="pr-2">
                        <v-text-field
                            v-model="milestone.name"
                            hide-details
                            prepend-icon="mdi-flag-triangle"
                            label="Name *"
                            :rules="[rules.required]"
                            required
                        >
                        </v-text-field>
                    </v-col>
                    <v-col cols="4">
                        <v-text-field
                            v-model.number="milestone.weight"
                            type="number"
                            min="0"
                            step="any"
                            :max="remainingWeight"
                            prepend-icon="mdi-percent"
                            label="Weight (%) *"
                            :rules="[
                                rules.requiredWeight,
                                rules.weightRange,
                                remainingWeightRule,
                            ]"
                            :hint="`Remaining: ${remainingWeight}%`"
                            persistent-hint
                            required
                        >
                        </v-text-field>
                    </v-col>
                </v-row>
                <v-row cols="12">
                    <v-textarea
                        v-model="milestone.description"
                        prepend-icon="mdi-text"
                        label="Description"
                        rows="1"
                        class="mx-3"
                        hide-details
                    >
                    </v-textarea>
                </v-row>
            </v-form>
        </v-card-text>
        <v-card-actions>
            <v-spacer />
            <v-btn
                color="primary"
                class="mb-4"
                @click="saveMilestone"
                rounded
                :loading="loading"
                :disabled="!valid"
            >
                SAVE
            </v-btn>
        </v-card-actions>
    </v-card>
</template>

<script>
import { mapMutations } from 'vuex'
import API from '@/services/api'
import { objDiff } from '@/helpers/objDiff.js'

export default {
    name: 'MilestonesForm',
    props: {
        milestone: {
            type: Object,
            default: () => ({}),
        },
        originalMilestone: {
            type: Object,
            default: () => ({}),
        },
        createForm: {
            type: Boolean,
            default: () => false,
        },
        settingId: {
            type: String,
            default: () => '',
        },
        remainingWeight: {
            type: Number,
            default: () => 100,
        },
    },
    data: () => ({
        loading: false,
        valid: false,
        rules: {
            required: v => !!v || 'The value is required',
            requiredWeight: v =>
                (v !== null && v !== undefined && v !== '') ||
                'The value is required',
            weightRange: v => {
                const numericValue = Number(v)
                if (Number.isNaN(numericValue)) {
                    return 'Enter a valid number'
                }
                return (
                    (numericValue >= 0 && numericValue <= 100) ||
                    'Only values from 0 to 100 are allowed'
                )
            },
        },
    }),
    computed: {
        milestoneDiff: function() {
            if (!this.createForm) {
                return objDiff(this.originalMilestone, this.milestone)
            } else {
                return null
            }
        },
    },
    methods: {
        ...mapMutations(['setErrorItems']),
        remainingWeightRule(value) {
            const numericValue = Number(value)
            if (Number.isNaN(numericValue)) {
                return true
            }
            const nextTotal = Number(
                (100 - this.remainingWeight + numericValue).toFixed(4)
            )
            if (nextTotal > 100) {
                return `The total weight cannot exceed 100%. Remaining: ${this.remainingWeight}%`
            }
            return true
        },
        async saveMilestone() {
            if (this.createForm) {
                await this.create()
            } else {
                await this.update()
            }
        },
        async create() {
            try {
                this.loading = true
                const milestone = await API.createMilestone({
                    settingId: this.settingId,
                    ...this.milestone,
                    weight: Number(this.milestone.weight),
                })
                this.$emit('addMilestone', milestone)
                this.$emit('close')
            } catch (error) {
                this.setErrorItems({
                    source: this.$options.name,
                    message: error.message,
                })
            } finally {
                this.loading = false
            }
        },
        async update() {
            try {
                this.loading = true
                const paramsToUpdate = { ...this.milestoneDiff }
                if (paramsToUpdate.weight !== undefined) {
                    paramsToUpdate.weight = Number(paramsToUpdate.weight)
                }
                const milestone = await API.updateMilestone({
                    settingId: this.settingId,
                    milestoneId: this.milestone.id,
                    ...paramsToUpdate,
                })
                this.$emit('replaceMilestone', milestone)
                this.$emit('close')
            } catch (error) {
                this.setErrorItems({
                    source: this.$options.name,
                    message: error.message,
                })
            } finally {
                this.loading = false
            }
        },
        close() {
            this.$emit('close')
        },
    },
}
</script>

<style></style>
